import {
  ODSButton,
  ODSColumn,
  ODSIcon,
  ODSLink,
  ODSRow,
  ODSText,
} from "@/atoms";
import classNames from "classnames";
import React, { forwardRef, useEffect, useState } from "react";

import { ODSNotificationProps } from "./ODSNotification.types";
import {
  generateNotificationIconName,
  getNotificationAriaLiveAttribute,
  getNotificationScheme,
  getNotificationSkin,
} from "./ODSNotification.utils";

const ODSNotification = forwardRef<HTMLDivElement, ODSNotificationProps>(
  (
    {
      className,
      variant = "banner",
      type,
      mode = "informative",
      buttonProps,
      showCloseButton = true,
      title,
      text,
      primaryLinkProps,
      link1Props,
      secondaryLinkProps,
      link2Props,
      opened = true,
      timeout = 0,
      onClose,
      iconProps,
      //eslint-disable-next-line
      showLinks,
      titleHeading = "h3",
      ...rest
    },
    ref
  ) => {
    /** link1FinalProps, link2FinalProps need to be used to handle the deprecated
     * `primaryLinkProps` and `secondaryLinkProps`. */
    const link1FinalProps = primaryLinkProps ?? link1Props;
    const link2FinalProps = secondaryLinkProps ?? link2Props;
    const notificationSkin = getNotificationSkin();
    const dataScheme = getNotificationScheme(variant);
    const ariaLive = getNotificationAriaLiveAttribute(type ?? mode);
    const [isMounted, updateIsMounted] = useState(opened);

    useEffect(() => {
      const previousValue = isMounted;
      const currentValue = opened;
      if (previousValue !== currentValue) updateIsMounted(currentValue);
    }, [opened]);

    useEffect(() => {
      if (timeout === 0) return;
      const unmountTimeout = setTimeout(() => onClose?.(), timeout);
      return () => clearTimeout(unmountTimeout);
    }, [opened, timeout]);

    // Derive a default aria-label from title, alternativelly the fallback from type.
    const defaultIconAriaLabel = `${title ?? type ?? mode} icon`;

    return (
      <ODSRow
        ref={ref}
        aria-live={ariaLive}
        data-scheme={dataScheme}
        className={classNames(
          `ODSNotification`,
          `ODSNotification-variant-${variant}`,
          `ODSNotification-mode-${type === "information" ? "informative" : (type ?? mode)}`,
          `ODSNotification-skin-${notificationSkin}`,
          className
        )}
        {...rest}
      >
        {isMounted ? (
          <ODSRow className="ODSNotification-MaxWidthWrapper">
            <ODSIcon
              name={generateNotificationIconName(type ?? mode)}
              selected
              className="ODSNotification-Icon"
              tabIndex={undefined}
              aria-label={defaultIconAriaLabel}
              {...iconProps}
            />

            <ODSRow className="ODSNotification-Content">
              <ODSRow className="ODSNotification-Body">
                <ODSColumn className="ODSNotification-Texts-Wrapper">
                  <ODSColumn className="ODSNotification-Texts">
                    {title ? (
                      <ODSText
                        as={titleHeading}
                        title={title}
                        className="ODSNotification-Title"
                      >
                        {title}
                      </ODSText>
                    ) : null}
                    {text ? (
                      <ODSText as="span" className="ODSNotification-Text">
                        {text}
                      </ODSText>
                    ) : null}
                  </ODSColumn>
                </ODSColumn>
                <ODSColumn className="ODSNotification-Spacing">
                  {showCloseButton ? (
                    <ODSButton
                      data-scheme={dataScheme}
                      buttonIcon="close-type-bold"
                      buttonType="iconOnly"
                      size="small"
                      variant="ghost"
                      aria-label="close"
                      onClick={onClose}
                      {...{
                        ...buttonProps,
                        className: classNames(
                          "ODSNotification-Button",
                          buttonProps?.className
                        ),
                      }}
                    />
                  ) : null}
                </ODSColumn>
              </ODSRow>
              {link1FinalProps || link2FinalProps ? (
                <ODSRow className="ODSNotification-Links">
                  {link1FinalProps ? (
                    <ODSLink {...link1FinalProps} type="secondary" />
                  ) : null}
                  {link2FinalProps ? (
                    <ODSLink {...link2FinalProps} type="secondary" />
                  ) : null}
                </ODSRow>
              ) : null}
            </ODSRow>
          </ODSRow>
        ) : null}
      </ODSRow>
    );
  }
);

ODSNotification.displayName = "ODSNotification";

export default ODSNotification;
